'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import Breadcrumb from '@/components/Breadcrumb';
import { createClient } from '@/lib/supabase/client';

interface StoricoRecord {
  id: string;
  configurazione_id: string;
  device_id: string;
  versione: string;
  configurazione_precedente: Record<string, any>;
  configurazione_nuova: Record<string, any>;
  diff_sommario: string;
  campi_modificati: string[];
  autore_nome: string;
  note_modifica: string;
  created_at: string;
  devices?: { hostname: string; ip_address: string; categoria: string };
  user_profiles?: { full_name: string };
}

interface ConfigurazioneDispositivo {
  id: string;
  device_id: string;
  titolo: string;
  descrizione: string;
  configurazione: Record<string, any>;
  versione: string;
  is_attiva: boolean;
  note: string;
  created_at: string;
  updated_at: string;
  devices?: { hostname: string; ip_address: string; categoria: string };
}

type TabType = 'storico' | 'configurazioni' | 'nuova';

export default function StoricoConfigurazioniContent() {
  const [activeTab, setActiveTab] = useState<TabType>('storico');
  const [storico, setStorico] = useState<StoricoRecord[]>([]);
  const [configurazioni, setConfigurazioni] = useState<ConfigurazioneDispositivo[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<StoricoRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState<{ id: string; hostname: string; ip_address: string }[]>([]);
  const [filterDevice, setFilterDevice] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const [newConf, setNewConf] = useState({ device_id: '', titolo: '', descrizione: '', configurazione: '{}', note: '' });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const supabase = createClient();
      const [storicoRes, confRes, devRes] = await Promise.all([
        supabase.from('storico_configurazioni').select('*, devices(hostname, ip_address, categoria)').order('created_at', { ascending: false }).limit(100),
        supabase.from('configurazioni_dispositivi').select('*, devices(hostname, ip_address, categoria)').order('updated_at', { ascending: false }),
        supabase.from('devices').select('id, hostname, ip_address').order('hostname'),
      ]);
      setStorico(storicoRes.data || []);
      setConfigurazioni(confRes.data || []);
      setDevices(devRes.data || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConf = async () => {
    if (!newConf.device_id || !newConf.titolo) {
      setSaveMsg('Dispositivo e titolo sono obbligatori');
      return;
    }
    let parsedConf: Record<string, any> = {};
    try {
      parsedConf = JSON.parse(newConf.configurazione);
    } catch {
      setSaveMsg('JSON configurazione non valido — verifica la sintassi');
      return;
    }
    setSaving(true);
    setSaveMsg('');
    try {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();

      const { data: existing } = await supabase
        .from('configurazioni_dispositivi')
        .select('id, versione, configurazione')
        .eq('device_id', newConf.device_id)
        .eq('is_attiva', true)
        .maybeSingle();

      if (existing) {
        await supabase.from('configurazioni_dispositivi').update({ is_attiva: false }).eq('id', existing.id);
      }

      const newVersion = existing ? incrementVersion(existing.versione) : '1.0';

      const { data: inserted, error } = await supabase
        .from('configurazioni_dispositivi')
        .insert({
          device_id: newConf.device_id,
          titolo: newConf.titolo,
          descrizione: newConf.descrizione,
          configurazione: parsedConf,
          versione: newVersion,
          is_attiva: true,
          note: newConf.note,
          created_by: user?.id,
        })
        .select()
        .single();

      if (error) { setSaveMsg('Errore nel salvataggio'); setSaving(false); return; }

      if (existing) {
        const campiModificati = getChangedFields(existing.configurazione, parsedConf);
        await supabase.from('storico_configurazioni').insert({
          configurazione_id: inserted.id,
          device_id: newConf.device_id,
          versione: newVersion,
          configurazione_precedente: existing.configurazione,
          configurazione_nuova: parsedConf,
          diff_sommario: `Aggiornamento da v${existing.versione} a v${newVersion}. Campi modificati: ${campiModificati.join(', ')}`,
          campi_modificati: campiModificati,
          autore_id: user?.id,
          autore_nome: user?.email || 'Utente',
          note_modifica: newConf.note,
        });
      }

      setSaveMsg('Configurazione salvata con successo!');
      setNewConf({ device_id: '', titolo: '', descrizione: '', configurazione: '{}', note: '' });
      fetchData();
      setActiveTab('configurazioni');
    } catch {
      setSaveMsg('Errore imprevisto');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConf = async (id: string, nome: string) => {
    if (!confirm(`Eliminare la configurazione "${nome}"?`)) return;
    try {
      const supabase = createClient();
      const { error } = await supabase.from('configurazioni_dispositivi').delete().eq('id', id);
      if (error) throw error;
      fetchData();
    } catch {
      // silent
    }
  };

  const incrementVersion = (v: string): string => {
    const parts = v.split('.');
    const minor = parseInt(parts[1] || '0') + 1;
    return `${parts[0]}.${minor}`;
  };

  const getChangedFields = (prev: Record<string, any>, next: Record<string, any>): string[] => {
    const changed: string[] = [];
    const allKeys = new Set([...Object.keys(prev), ...Object.keys(next)]);
    allKeys.forEach(k => {
      if (JSON.stringify(prev[k]) !== JSON.stringify(next[k])) changed.push(k);
    });
    return changed;
  };

  const filteredStorico = storico.filter(s => {
    const matchDevice = !filterDevice || s.device_id === filterDevice;
    const matchFrom = !filterDateFrom || new Date(s.created_at) >= new Date(filterDateFrom);
    const matchTo = !filterDateTo || new Date(s.created_at) <= new Date(filterDateTo + 'T23:59:59');
    return matchDevice && matchFrom && matchTo;
  });

  const renderDiff = (record: StoricoRecord) => {
    const prev = record.configurazione_precedente || {};
    const next = record.configurazione_nuova || {};
    const allKeys = Array.from(new Set([...Object.keys(prev), ...Object.keys(next)]));

    return (
      <div className="space-y-2">
        {allKeys.map(key => {
          const prevVal = JSON.stringify(prev[key] ?? '—');
          const nextVal = JSON.stringify(next[key] ?? '—');
          const changed = prevVal !== nextVal;
          const isNew = !(key in prev);
          const isRemoved = !(key in next);
          return (
            <div key={key} className={`p-3 rounded-xl border text-sm ${changed ? (isNew ? 'bg-emerald-500/5 border-emerald-500/20' : isRemoved ? 'bg-red-500/5 border-red-500/20' : 'bg-amber-500/5 border-amber-500/20') : 'bg-surface border-border'}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono font-bold text-white text-xs">{key}</span>
                {isNew && <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-bold">NUOVO</span>}
                {isRemoved && <span className="text-xs px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 font-bold">RIMOSSO</span>}
                {changed && !isNew && !isRemoved && <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 font-bold">MODIFICATO</span>}
              </div>
              {changed && !isNew && !isRemoved && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2 rounded bg-red-500/10 border border-red-500/20">
                    <p className="text-xs text-muted-color mb-0.5">Prima</p>
                    <p className="font-mono text-xs text-red-300 break-all">{prevVal}</p>
                  </div>
                  <div className="p-2 rounded bg-emerald-500/10 border border-emerald-500/20">
                    <p className="text-xs text-muted-color mb-0.5">Dopo</p>
                    <p className="font-mono text-xs text-emerald-300 break-all">{nextVal}</p>
                  </div>
                </div>
              )}
              {isNew && <p className="font-mono text-xs text-emerald-300 break-all">{nextVal}</p>}
              {isRemoved && <p className="font-mono text-xs text-red-300 break-all">{prevVal}</p>}
              {!changed && <p className="font-mono text-xs text-secondary-color break-all">{nextVal}</p>}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto">
      {/* Breadcrumb */}
      <Breadcrumb items={[{ label: 'Storico Configurazioni' }]} />

      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl bg-sky-500/10 border border-sky-500/30 flex items-center justify-center">
          <Icon name="ClockIcon" size={26} className="text-sky-400" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-white">Storico Configurazioni</h1>
          <p className="text-lg text-secondary-color mt-0.5">Cronologia modifiche con confronto visuale (diff)</p>
        </div>
        <div className="ml-auto">
          <Link href="/guide" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-surface border border-border text-secondary-color hover:text-white text-sm font-semibold transition-all">
            <Icon name="BookOpenIcon" size={16} />
            Guide
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-surface rounded-xl border border-border mb-6 w-fit">
        {([
          { id: 'storico', label: 'Storico Modifiche', icon: 'ClockIcon' },
          { id: 'configurazioni', label: 'Configurazioni Attive', icon: 'Cog6ToothIcon' },
          { id: 'nuova', label: 'Nuova Configurazione', icon: 'PlusCircleIcon' },
        ] as { id: TabType; label: string; icon: string }[]).map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === tab.id ? 'bg-sky-500/10 text-sky-400 border border-sky-500/30' : 'text-secondary-color hover:text-white'}`}
          >
            <Icon name={tab.icon as any} size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="grid grid-cols-1 gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-20 bg-surface rounded-2xl animate-pulse" />
          ))}
        </div>
      )}

      {/* STORICO TAB */}
      {!loading && activeTab === 'storico' && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2">
            <div className="space-y-3 mb-4">
              <select
                value={filterDevice}
                onChange={e => setFilterDevice(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl bg-surface border border-border text-white text-sm focus:outline-none focus:border-sky-500/50"
              >
                <option value="">Tutti i dispositivi</option>
                {devices.map(d => <option key={d.id} value={d.id}>{d.hostname} ({d.ip_address})</option>)}
              </select>
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)}
                  className="px-3 py-2 rounded-xl bg-surface border border-border text-white text-xs focus:outline-none focus:border-sky-500/50" />
                <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)}
                  className="px-3 py-2 rounded-xl bg-surface border border-border text-white text-xs focus:outline-none focus:border-sky-500/50" />
              </div>
            </div>

            <p className="text-xs text-muted-color mb-3">{filteredStorico.length} modifiche trovate</p>

            <div className="space-y-2 max-h-[600px] overflow-y-auto scrollbar-thin pr-1">
              {filteredStorico.length === 0 && (
                <div className="p-6 rounded-2xl bg-surface border border-border text-center">
                  <Icon name="ClockIcon" size={32} className="text-muted-color mx-auto mb-2" />
                  <p className="text-secondary-color text-sm">Nessuna modifica trovata</p>
                </div>
              )}
              {filteredStorico.map(record => (
                <button
                  key={record.id}
                  onClick={() => setSelectedRecord(record)}
                  className={`w-full text-left p-4 rounded-xl border transition-all ${selectedRecord?.id === record.id ? 'bg-sky-500/10 border-sky-500/30' : 'bg-surface border-border hover:border-sky-500/30'}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-sm font-bold text-white truncate">{record.devices?.hostname || 'Dispositivo'}</p>
                    <span className="text-xs text-muted-color flex-shrink-0">v{record.versione}</span>
                  </div>
                  <p className="text-xs text-secondary-color mb-1 truncate">{record.diff_sommario || 'Modifica configurazione'}</p>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-color">{record.autore_nome}</span>
                    <span className="text-xs text-muted-color">{new Date(record.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  {record.campi_modificati?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {record.campi_modificati.slice(0, 3).map(c => (
                        <span key={c} className="text-xs px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/20 text-amber-400">{c}</span>
                      ))}
                      {record.campi_modificati.length > 3 && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-surface border border-border text-muted-color">+{record.campi_modificati.length - 3}</span>
                      )}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3">
            {!selectedRecord && (
              <div className="p-8 rounded-2xl bg-surface border border-border text-center h-full flex flex-col items-center justify-center">
                <Icon name="ArrowsRightLeftIcon" size={48} className="text-muted-color mb-4 opacity-50" />
                <p className="text-secondary-color text-lg">Seleziona una modifica per vedere il confronto</p>
              </div>
            )}
            {selectedRecord && (
              <div className="bg-surface rounded-2xl border border-border overflow-hidden">
                <div className="p-5 border-b border-border">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-bold text-white mb-1">{selectedRecord.devices?.hostname || 'Dispositivo'}</h3>
                      <p className="text-sm text-secondary-color">{selectedRecord.diff_sommario}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-sky-400">v{selectedRecord.versione}</p>
                      <p className="text-xs text-muted-color">{new Date(selectedRecord.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-3">
                    <div className="flex items-center gap-2">
                      <Icon name="UserIcon" size={14} className="text-muted-color" />
                      <span className="text-xs text-secondary-color">{selectedRecord.autore_nome}</span>
                    </div>
                    {selectedRecord.note_modifica && (
                      <div className="flex items-center gap-2">
                        <Icon name="ChatBubbleLeftIcon" size={14} className="text-muted-color" />
                        <span className="text-xs text-secondary-color italic">{selectedRecord.note_modifica}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border">
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded bg-emerald-500/30 border border-emerald-500/50" />
                      <span className="text-xs text-muted-color">Nuovo / Aggiunto</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded bg-red-500/30 border border-red-500/50" />
                      <span className="text-xs text-muted-color">Rimosso</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded bg-amber-500/30 border border-amber-500/50" />
                      <span className="text-xs text-muted-color">Modificato</span>
                    </div>
                  </div>
                </div>
                <div className="p-5 max-h-[500px] overflow-y-auto scrollbar-thin">
                  {renderDiff(selectedRecord)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CONFIGURAZIONI ATTIVE TAB */}
      {!loading && activeTab === 'configurazioni' && (
        <div className="space-y-4">
          {configurazioni.length === 0 && (
            <div className="p-8 rounded-2xl bg-surface border border-border text-center">
              <Icon name="Cog6ToothIcon" size={48} className="text-muted-color mx-auto mb-4 opacity-50" />
              <p className="text-secondary-color text-lg mb-4">Nessuna configurazione salvata</p>
              <button onClick={() => setActiveTab('nuova')} className="px-6 py-2.5 rounded-xl bg-sky-500/10 border border-sky-500/30 text-sky-400 font-semibold text-sm hover:bg-sky-500/20 transition-all">
                Aggiungi la prima configurazione
              </button>
            </div>
          )}
          {configurazioni.map(conf => (
            <div key={conf.id} className="bg-surface border border-border rounded-xl p-4 hover:border-sky-500/30 transition-colors">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${conf.is_attiva ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-500/20 text-gray-400'}`}>
                      {conf.is_attiva ? 'Attiva' : 'Storica'}
                    </span>
                    <span className="text-xs text-muted-color font-mono">v{conf.versione}</span>
                  </div>
                  <p className="text-sm font-semibold text-white truncate">{conf.titolo}</p>
                  <p className="text-xs text-muted-color">{(conf.devices as any)?.hostname || 'N/D'}</p>
                </div>
                <button
                  onClick={() => handleDeleteConf(conf.id, conf.titolo)}
                  className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-color hover:text-red-400 transition-colors flex-shrink-0 ml-2"
                  title="Elimina configurazione"
                >
                  <Icon name="TrashIcon" size={14} />
                </button>
              </div>
              {conf.descrizione && <p className="text-xs text-muted-color mb-2">{conf.descrizione}</p>}
              <div className="bg-surface-elevated rounded-lg p-2 font-mono text-xs text-secondary-color overflow-auto max-h-24 scrollbar-thin">
                {JSON.stringify(conf.configurazione, null, 2)}
              </div>
              <p className="text-xs text-muted-color mt-2">{new Date(conf.updated_at).toLocaleString('it-IT')}</p>
            </div>
          ))}
        </div>
      )}

      {/* NUOVA CONFIGURAZIONE TAB */}
      {!loading && activeTab === 'nuova' && (
        <div className="max-w-2xl">
          <div className="p-6 rounded-2xl bg-surface border border-border space-y-5">
            <h2 className="text-xl font-bold text-white">Nuova Configurazione Dispositivo</h2>
            <p className="text-sm text-secondary-color">Salva la configurazione attuale di un dispositivo. Se esiste già una configurazione attiva per questo dispositivo, verrà archiviata e il confronto sarà salvato nello storico.</p>

            <div>
              <label className="block text-sm font-semibold text-white mb-2">Dispositivo *</label>
              <select
                value={newConf.device_id}
                onChange={e => setNewConf(p => ({ ...p, device_id: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl bg-surface-elevated border border-border text-white text-sm focus:outline-none focus:border-sky-500/50"
              >
                <option value="">Seleziona dispositivo...</option>
                {devices.map(d => <option key={d.id} value={d.id}>{d.hostname} ({d.ip_address})</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-white mb-2">Titolo configurazione *</label>
              <input
                type="text"
                value={newConf.titolo}
                onChange={e => setNewConf(p => ({ ...p, titolo: e.target.value }))}
                placeholder="es. Configurazione dopo aggiornamento firmware"
                className="w-full px-4 py-3 rounded-xl bg-surface-elevated border border-border text-white text-sm placeholder-muted-color focus:outline-none focus:border-sky-500/50"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-white mb-2">Descrizione</label>
              <input
                type="text"
                value={newConf.descrizione}
                onChange={e => setNewConf(p => ({ ...p, descrizione: e.target.value }))}
                placeholder="Breve descrizione delle modifiche"
                className="w-full px-4 py-3 rounded-xl bg-surface-elevated border border-border text-white text-sm placeholder-muted-color focus:outline-none focus:border-sky-500/50"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-white mb-2">
                Configurazione (JSON) *
                <span className="text-xs text-muted-color font-normal ml-2">Inserisci i parametri in formato JSON</span>
              </label>
              <textarea
                value={newConf.configurazione}
                onChange={e => setNewConf(p => ({ ...p, configurazione: e.target.value }))}
                rows={8}
                placeholder={'{\n  "ip": "192.168.1.100",\n  "subnet": "255.255.255.0",\n  "gateway": "192.168.1.1",\n  "porta": 9100\n}'}
                className="w-full px-4 py-3 rounded-xl bg-surface-elevated border border-border text-white text-sm font-mono placeholder-muted-color focus:outline-none focus:border-sky-500/50 resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-white mb-2">Note modifica</label>
              <input
                type="text"
                value={newConf.note}
                onChange={e => setNewConf(p => ({ ...p, note: e.target.value }))}
                placeholder="es. Cambio gateway dopo sostituzione router"
                className="w-full px-4 py-3 rounded-xl bg-surface-elevated border border-border text-white text-sm placeholder-muted-color focus:outline-none focus:border-sky-500/50"
              />
            </div>

            {saveMsg && (
              <div className={`p-3 rounded-xl text-sm font-semibold ${saveMsg.includes('successo') ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400' : 'bg-red-500/10 border border-red-500/30 text-red-400'}`}>
                {saveMsg}
              </div>
            )}

            <button
              onClick={handleSaveConf}
              disabled={saving}
              className="w-full py-3 rounded-xl bg-sky-500/10 border border-sky-500/30 text-sky-400 font-bold text-base hover:bg-sky-500/20 transition-all disabled:opacity-50"
            >
              {saving ? 'Salvataggio...' : 'Salva Configurazione'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
